import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';
import { api, bannerImageUrl } from '@/lib/api';
import { heroSlides } from '@/data/homeContent';
import { cn } from '@/lib/utils';
import type { BannerSlide } from '@/types';

const INTERVALO_MS = 7000;

function fallbackDesdeEstatico() {
  return heroSlides.map((s) => ({
    id: s.id,
    etiqueta: s.tag,
    titulo: s.headline,
    subtitulo: s.body,
    imagenDesktop: '',
    imagenMovil: null,
    urlDestino: s.cta.to,
    btn1: { texto: s.cta.label, url: s.cta.to },
    btn2: { texto: s.ctaSecondary.label, url: s.ctaSecondary.to },
    _estatico: true as const,
  }));
}

type SlideItem = BannerSlide | ReturnType<typeof fallbackDesdeEstatico>[number];

function enlaceSlide(slide: SlideItem): string | null {
  if (slide.urlDestino) return slide.urlDestino;
  if (slide.btn1?.url) return slide.btn1.url;
  return null;
}

function SlideImagen({
  slide,
  activo,
  eager,
}: {
  slide: SlideItem;
  activo: boolean;
  eager: boolean;
}) {
  const esEstatico = '_estatico' in slide && slide._estatico;
  const desktop = !esEstatico && slide.imagenDesktop ? bannerImageUrl(slide.imagenDesktop) : null;
  const movil =
    !esEstatico && (slide.imagenMovil || slide.imagenDesktop)
      ? bannerImageUrl(slide.imagenMovil || slide.imagenDesktop)
      : desktop;
  const href = enlaceSlide(slide);

  if (desktop) {
    const img = (
      <picture>
        <source media="(max-width: 1023px)" srcSet={movil ?? desktop} />
        <img
          src={desktop}
          alt={slide.titulo?.trim() || 'Promoción Alarcom'}
          className="hero-banner-img"
          loading={eager ? 'eager' : 'lazy'}
          fetchPriority={eager ? 'high' : 'auto'}
          decoding="async"
        />
      </picture>
    );

    if (href) {
      return (
        <Link to={href} className="block h-full w-full" tabIndex={activo ? 0 : -1} aria-hidden={!activo}>
          {img}
        </Link>
      );
    }
    return img;
  }

  return (
    <div className="flex h-full w-full flex-col justify-center bg-gradient-to-br from-surface-900 via-brand-950 to-surface-900 px-6 py-8 sm:px-10 lg:px-[max(2.5rem,calc((100vw-80rem)/2+2rem))]">
      {slide.etiqueta && (
        <span className="mb-2 inline-block w-fit rounded bg-brand-600/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
          {slide.etiqueta}
        </span>
      )}
      {slide.titulo && (
        <h2 className="max-w-lg text-xl font-bold leading-tight text-white sm:text-2xl lg:text-3xl">
          {slide.titulo}
        </h2>
      )}
      {slide.subtitulo && (
        <p className="mt-2 max-w-md text-sm leading-relaxed text-white/75 sm:text-base">{slide.subtitulo}</p>
      )}
      {(slide.btn1 || slide.btn2) && (
        <div className="mt-5 flex flex-wrap gap-2">
          {slide.btn1 && (
            <Link to={slide.btn1.url} className="btn-primary" tabIndex={activo ? 0 : -1}>
              {slide.btn1.texto}
            </Link>
          )}
          {slide.btn2 && (
            <Link to={slide.btn2.url} className="btn-outline-light" tabIndex={activo ? 0 : -1}>
              {slide.btn2.texto}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export function HeroBannerSlider() {
  const [slides, setSlides] = useState<SlideItem[]>([]);
  const [index, setIndex] = useState(0);
  const [cargando, setCargando] = useState(true);
  const [autoplay, setAutoplay] = useState(true);
  const [hover, setHover] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const touchStart = useRef<number | null>(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const handler = () => setReducedMotion(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    api
      .banners()
      .then((items) => setSlides(items.length > 0 ? items : fallbackDesdeEstatico()))
      .catch(() => setSlides(fallbackDesdeEstatico()))
      .finally(() => setCargando(false));
  }, []);

  const total = slides.length;

  const irA = useCallback(
    (next: number) => {
      if (total <= 0) return;
      setIndex(((next % total) + total) % total);
    },
    [total],
  );

  const siguiente = useCallback(() => irA(index + 1), [index, irA]);
  const anterior = useCallback(() => irA(index - 1), [index, irA]);

  useEffect(() => {
    if (total <= 1 || !autoplay || hover || reducedMotion) return;
    const t = setInterval(siguiente, INTERVALO_MS);
    return () => clearInterval(t);
  }, [total, autoplay, hover, reducedMotion, siguiente]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStart.current = e.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStart.current === null || total <= 1) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStart.current;
    if (Math.abs(dx) > 48) {
      if (dx < 0) siguiente();
      else anterior();
    }
    touchStart.current = null;
  };

  if (cargando) {
    return (
      <section className="border-b border-surface-200" aria-busy="true">
        <div className="hero-banner-stage animate-pulse bg-surface-800" />
        <div className="border-t border-surface-100 bg-white py-2.5">
          <div className="container-app flex justify-center">
            <span className="h-1 w-8 rounded-full bg-surface-200" />
          </div>
        </div>
      </section>
    );
  }

  if (total === 0) return null;

  return (
    <section
      className="border-b border-surface-200"
      aria-roledescription="carrusel"
      aria-label="Promociones"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div
        className="hero-banner-stage"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {slides.map((slide, i) => (
          <div
            key={slide.id}
            className={cn(
              'hero-banner-slide',
              i === index ? 'hero-banner-slide-active' : 'hero-banner-slide-inactive',
            )}
            aria-hidden={i !== index}
          >
            <SlideImagen slide={slide} activo={i === index} eager={i === 0} />
          </div>
        ))}

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={anterior}
              className="hero-banner-nav left-0 sm:left-4 lg:left-8"
              aria-label="Promoción anterior"
            >
              <ChevronLeft className="h-6 w-6 drop-shadow-md" />
            </button>
            <button
              type="button"
              onClick={siguiente}
              className="hero-banner-nav right-0 sm:right-4 lg:right-8"
              aria-label="Siguiente promoción"
            >
              <ChevronRight className="h-6 w-6 drop-shadow-md" />
            </button>
          </>
        )}
      </div>

      {total > 1 && (
        <div className="border-t border-surface-100 bg-white py-2.5">
          <div className="container-app flex items-center justify-center gap-3">
            <div className="flex items-center gap-1.5" role="tablist" aria-label="Seleccionar promoción">
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`Promoción ${i + 1} de ${total}`}
                  onClick={() => irA(i)}
                  className={cn(
                    'h-1 rounded-full transition-all duration-300',
                    i === index ? 'w-8 bg-brand-600' : 'w-4 bg-surface-300 hover:bg-surface-400',
                  )}
                />
              ))}
            </div>
            {!reducedMotion && (
              <button
                type="button"
                onClick={() => setAutoplay((p) => !p)}
                className="rounded p-1 text-surface-400 hover:bg-surface-100 hover:text-surface-600"
                aria-label={autoplay ? 'Pausar carrusel' : 'Reanudar carrusel'}
              >
                {autoplay ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
